import { Button } from '@brightdrop/bd-ui';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { memo } from 'react';

import type { AlertRecord } from '~/api/generated-api';
import ChevronDownIcon from '~/assets/icons/new/chevron-down.svg?react';
import TranslatedLabel from '~/common/components/TranslatedLabel/TranslatedLabel';

import GroupNotificationCard from '../Cards/GroupNotificationCard/GroupNotificationCard';
import NotificationCard from '../Cards/NotificationCard/NotificationCard';
import NotificationHeader from '../NotificationHeader/NotificationHeader';

interface NotificationListProps {
  items: AlertRecord[];
  context: 'popover' | 'page';
  type?: 'section' | 'group';
  inPinnedSection?: boolean;
  title?: string;
  showLessCallback?: () => void;
  onDismissNotification?: (alert: AlertRecord) => void;
  onClose?: () => void;
  showLessPinned?: boolean;
}

const NotificationList = memo(function NotificationList({
  items,
  context,
  type = 'section',
  inPinnedSection = false,
  title,
  showLessCallback = () => {},
  onDismissNotification,
  onClose,
  showLessPinned = false,
}: NotificationListProps) {
  const renderHeader = (title: string, end = false) => {
    /* Make the titleinto a sticky header if in notification center */
    if (context === 'page') {
      if (type === 'section' && end) {
        return null;
      }

      const id = end
        ? `notification-list-${title.toLowerCase().replaceAll(' ', '-')}-end-title`
        : `notification-list-${title.toLowerCase().replaceAll(' ', '-')}-title`;

      return (
        <NotificationHeader
          id={id}
          label={end ? '' : title}
          type={type}
          showLessCallback={showLessCallback}
          inPinnedSection={inPinnedSection}
          showPinnedSectionButton={!showLessPinned}
        />
      );
    }

    if (!end) {
      /* Make the title into a div with a button if in popover and not at the end of a group */
      return (
        <Box
          py="8px"
          display="flex"
          justifyContent="space-between"
          key={`header-${title}`}
          data-testid="notification-list-header"
        >
          <Typography variant="small_body_medium">{title}</Typography>
          <Button
            variant="button-link"
            onClick={showLessCallback}
            endIcon={
              <ChevronDownIcon style={{ transform: 'rotate(180deg)' }} />
            }
            iconColorRule="stroke"
          >
            <TranslatedLabel
              translationKey="common:showLess"
              typographyVariant="label_medium"
            />
          </Button>
        </Box>
      );
    }
  };

  const renderItems = () => {
    return items.map((item) => {
      /* If group alert count is 1, render as a single notification card */
      const isGroup =
        context === 'popover' ? item.feedActiveCount > 1 : item.alertCount > 1;

      return item.type === 'GROUP' ? (
        isGroup ? (
          <GroupNotificationCard
            key={item.id}
            item={item}
            context={context}
            onClose={onClose}
          />
        ) : (
          <NotificationCard
            key={item.lastGroupEvent.id}
            item={{
              ...item,
              event: item.lastGroupEvent.event,
              id: item.lastGroupEvent.id,
            }}
            context={context}
            inPinnedSection={inPinnedSection}
            onDismissNotification={onDismissNotification}
            onClose={onClose}
          />
        )
      ) : (
        <NotificationCard
          key={item.id}
          item={item}
          context={context}
          inPinnedSection={inPinnedSection}
          onDismissNotification={onDismissNotification}
          onClose={onClose}
        />
      );
    });
  };

  return (
    <>
      {title && renderHeader(title)}
      {renderItems()}
      {title && renderHeader(title, true)}
    </>
  );
});

export default NotificationList;
